import React from 'react';

import { Button, Input, Modal, Select, Table } from 'antd';

import StatusBlock from 'containers/StatusBlock';

import { callApiAsync } from 'services/util';

import { Notify } from 'util/notify';
import OptionsUtils from 'util/OptionsUtils';
import { isArray, isEmpty } from 'util/utils';

import { USERS_TYPE } from 'constants/Common';
import CommonApi from 'constants/CommonApi';

import './salespersonHandover.less';

const sellerLabel = (seller) => {
  const name = !isEmpty(seller?.name) ? seller.name : [seller?.firstName, seller?.lastName].filter(Boolean).join(' ');
  return seller?.isActive === false ? `${name} (Inactive)` : name;
};

const partyWord = (count) => (count === 1 ? 'party' : 'parties');

// Spreadsheet cells arrive with stray casing, padding and doubled spaces.
const normalise = (value) => `${value ?? ''}`.trim().toLowerCase().replace(/\s+/g, ' ');

const matchesName = (companyName, term) => {
  const name = normalise(companyName);
  return !isEmpty(name) && (name === term || name.includes(term));
};

// The arrow completes only once both ends are chosen, so the bar shows at a
// glance whether the transfer is fully specified and which way it runs.
const TransferArrow = ({ ready }) => (
  <svg width="62" height="12" viewBox="0 0 62 12" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M1 6h54"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray={ready ? undefined : '3 5'}
    />
    <path
      d="M54 1.5L59.5 6 54 10.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function SalespersonHandover() {
  const [allSellers, setAllSellers] = React.useState([]);
  const [activeSellers, setActiveSellers] = React.useState([]);
  const [fromSeller, setFromSeller] = React.useState(undefined);
  const [toSeller, setToSeller] = React.useState(undefined);
  const [parties, setParties] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [showMissing, setShowMissing] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [assigning, setAssigning] = React.useState(false);
  const requestRef = React.useRef(0);

  React.useEffect(() => {
    const loadSellers = async () => {
      const [all, active] = await Promise.all([
        OptionsUtils.getSellersList({ includeInactive: true }),
        OptionsUtils.getSellersList(),
      ]);
      setAllSellers(isArray(all) ? all : []);
      setActiveSellers(isArray(active) ? active : []);
    };
    loadSellers();
  }, []);

  const loadParties = React.useCallback(async (seller) => {
    const requestId = ++requestRef.current;
    if (isEmpty(seller)) {
      setParties([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [err, res] = await callApiAsync({ ...CommonApi.SellerHandover.parties, request: { seller } });
    // A newer request has been issued since this one started -- drop this response.
    if (requestId !== requestRef.current) return;
    setLoading(false);
    setParties(!err && isArray(res?.data?.list) ? res.data.list : []);
  }, []);

  const clearSearch = () => {
    setSearch('');
    setShowMissing(false);
  };

  const onFromChange = (value) => {
    setFromSeller(value);
    setSelectedIds([]);
    clearSearch();
    if (value === toSeller) setToSeller(undefined);
    loadParties(value);
  };

  // The box holds whatever was typed or pasted, verbatim and editable. One line
  // is an ordinary search; a pasted spreadsheet column is many lines, and each
  // becomes a name to match. Duplicates collapse so the tally stays honest.
  const searchTerms = React.useMemo(
    () => Array.from(new Set(search.split(/[\r\n]+/).map(normalise).filter(Boolean))),
    [search],
  );
  const isListSearch = searchTerms.length > 1;

  const visibleParties = React.useMemo(() => {
    if (!searchTerms.length) return parties;
    if (isListSearch) {
      return parties.filter((party) => searchTerms.some((term) => matchesName(party.companyName, term)));
    }
    const term = searchTerms[0];
    return parties.filter(
      (party) =>
        `${party.companyName || ''}`.toLowerCase().includes(term) ||
        `${party.vendorCode || ''}`.toLowerCase().includes(term),
    );
  }, [parties, searchTerms, isListSearch]);

  // Names the spreadsheet listed that this salesperson has no party for -- the
  // operator needs to see these, not silently get fewer rows than they pasted.
  const missingNames = React.useMemo(() => {
    if (!isListSearch) return [];
    return searchTerms.filter((term) => !parties.some((party) => matchesName(party.companyName, term)));
  }, [searchTerms, isListSearch, parties]);

  // The seller endpoint also returns admins and master-sales users; parties belong
  // to salespersons (type 8), so only those may be either end of a handover.
  const fromSellerOptions = React.useMemo(
    () => allSellers.filter((seller) => seller.type === USERS_TYPE.SELLER),
    [allSellers],
  );

  const toSellerOptions = React.useMemo(
    () => activeSellers.filter((seller) => seller.type === USERS_TYPE.SELLER && seller.id !== fromSeller),
    [activeSellers, fromSeller],
  );

  const doAssign = async () => {
    setAssigning(true);
    const [err, res] = await callApiAsync({
      ...CommonApi.SellerHandover.assign,
      request: { fromSeller, toSeller, accounts: selectedIds },
    });
    setAssigning(false);

    // callApi already surfaces the backend's message as an error toast, so no extra
    // notification is needed here -- but the write may have partly succeeded, so re-read
    // the list rather than assume nothing changed.
    if (err) {
      loadParties(fromSeller);
      return;
    }

    const target = activeSellers.find((seller) => seller.id === toSeller);
    const moved = res?.data?.updated ?? selectedIds.length;
    Notify.success({ message: `${moved} ${partyWord(moved)} assigned to ${sellerLabel(target)}` });
    setSelectedIds([]);
    loadParties(fromSeller);
  };

  const confirmAssign = () => {
    const target = activeSellers.find((seller) => seller.id === toSeller);
    const count = selectedIds.length;
    Modal.confirm({
      title: `Assign ${count} ${partyWord(count)} to ${sellerLabel(target)}?`,
      content: 'The login users of these parties will also see the new salesperson.',
      okText: `Assign ${count} ${partyWord(count)}`,
      cancelText: 'Cancel',
      onOk: doAssign,
    });
  };

  const columns = [
    { title: 'Company Name', dataIndex: 'companyName', render: (text) => <span>{text || '-'}</span> },
    {
      title: 'User',
      dataIndex: 'userName',
      width: 220,
      render: (text, record) => (
        <span className="handoverUser">
          {isEmpty(text) ? '-' : text}
          {record.userCount > 1 && <span className="handoverUserMore">+{record.userCount - 1}</span>}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 130,
      render: (isActive) => (
        <StatusBlock status={isActive ? 'active' : 'InActive'} title={isActive ? 'Active' : 'Inactive'} />
      ),
    },
    { title: 'City', dataIndex: 'city', width: 160, render: (text) => <span>{text || '-'}</span> },
    { title: 'Country', dataIndex: 'country', width: 160, render: (text) => <span>{text || '-'}</span> },
  ];

  const hasFrom = !isEmpty(fromSeller);
  const isReady = hasFrom && !isEmpty(toSeller);
  const canAssign = isReady && selectedIds.length > 0 && !assigning;
  const isFiltered = searchTerms.length > 0 && visibleParties.length !== parties.length;

  const emptyText = () => {
    if (!hasFrom) return 'Choose a salesperson to see their parties';
    if (isListSearch) return 'None of these names match this salesperson’s parties';
    return 'No parties found';
  };

  return (
    <div className="configurationWrapper handoverWrapper">
      <h2 className="handoverTitle">Salesperson Handover</h2>

      <div className="handoverBar">
        <div className="handoverField">
          <label htmlFor="handoverFrom">From salesperson</label>
          <Select
            id="handoverFrom"
            placeholder="Select salesperson"
            value={fromSeller}
            onChange={onFromChange}
            showSearch
            allowClear
            optionFilterProp="children"
          >
            {fromSellerOptions.map((seller) => (
              <Select.Option key={seller.id} value={seller.id}>
                {sellerLabel(seller)}
              </Select.Option>
            ))}
          </Select>
          <span className="handoverCaption">
            {hasFrom && !loading ? `${parties.length} ${partyWord(parties.length)}` : ''}
          </span>
        </div>

        <div className={`handoverArrow${isReady ? ' isReady' : ''}`}>
          <TransferArrow ready={isReady} />
        </div>

        <div className="handoverField">
          <label htmlFor="handoverTo">To salesperson</label>
          <Select
            id="handoverTo"
            placeholder="Select salesperson"
            value={toSeller}
            onChange={setToSeller}
            disabled={!hasFrom}
            showSearch
            allowClear
            optionFilterProp="children"
          >
            {toSellerOptions.map((seller) => (
              <Select.Option key={seller.id} value={seller.id}>
                {sellerLabel(seller)}
              </Select.Option>
            ))}
          </Select>
          <span className={`handoverCaption${selectedIds.length > 0 ? ' isCounting' : ''}`}>
            {selectedIds.length > 0 ? `Moving ${selectedIds.length}` : ''}
          </span>
        </div>

        <div className="handoverAction">
          <Button type="primary" disabled={!canAssign} loading={assigning} onClick={confirmAssign}>
            {selectedIds.length > 0 ? `Assign ${selectedIds.length} ${partyWord(selectedIds.length)}` : 'Assign'}
          </Button>
        </div>
      </div>

      <div className="handoverToolbar">
        <Input.TextArea
          className="handoverSearch"
          placeholder="Search a company, or paste a column of company names from Excel"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autosize={{ minRows: 1, maxRows: 6 }}
        />
        {isFiltered && (
          <span className="handoverShowing">
            Showing {visibleParties.length} of {parties.length}
          </span>
        )}
      </div>

      {isListSearch && (
        <div className="handoverPasted">
          <div className="handoverPastedSummary">
            <strong>
              {searchTerms.length} {searchTerms.length === 1 ? 'name' : 'names'}
            </strong>
            <span className="handoverPastedHit">{searchTerms.length - missingNames.length} matched</span>
            {missingNames.length > 0 && (
              <button type="button" className="handoverPastedMiss" onClick={() => setShowMissing(!showMissing)}>
                {missingNames.length} not found
              </button>
            )}
            <button type="button" className="handoverPastedClear" onClick={clearSearch}>
              Clear
            </button>
          </div>
          {showMissing && missingNames.length > 0 && (
            <ul className="handoverMissingList">
              {missingNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Table
        className="gx-table-responsive"
        columns={columns}
        dataSource={visibleParties}
        loading={loading}
        pagination={false}
        scroll={{ y: 420 }}
        rowKey={(record) => record.id}
        rowSelection={{ selectedRowKeys: selectedIds, onChange: setSelectedIds }}
        locale={{ emptyText: emptyText() }}
      />
    </div>
  );
}

export default SalespersonHandover;
